import type { PublicProductSummary } from '@repo/api-contracts';
import useEmblaCarousel from 'embla-carousel-react';
import { type ReactElement, useEffect, useState } from 'react';
import { FeaturedCard } from './featured-card';
import { FeaturedControls } from './featured-controls';
import { FeaturedCopy } from './featured-copy';

/** 이달의 상품으로 싣는 추천 상품 수 — 추천 목록 앞에서부터. */
const MAX_FEATURED = 5;

interface HomeFeaturedProps {
  /** 추천 목록 — 앞에서부터 최대 다섯 장. 비어 있으면 섹션을 그리지 않는다. */
  readonly products: readonly PublicProductSummary[];
}

/**
 * HOME 이달의 상품 — 화면 폭을 채우는 흐린 상품 사진 배경 위 왼쪽에 지금 장의 큰 상품명·소개·가격,
 * 오른쪽에 사진 카드 슬라이드(다음 장이 옆에 살짝 보인다). 장을 넘기면 왼쪽 문구와 배경이 함께 바뀐다.
 */
export const HomeFeatured = ({ products }: HomeFeaturedProps): ReactElement | null => {
  const items = products.slice(0, MAX_FEATURED);
  const canSlide = items.length > 1;
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', loop: canSlide });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (emblaApi === undefined) return undefined;
    const syncSelected = (): void => setSelectedIndex(emblaApi.selectedScrollSnap());
    syncSelected();
    emblaApi.on('select', syncSelected).on('reInit', syncSelected);
    return () => {
      emblaApi.off('select', syncSelected).off('reInit', syncSelected);
    };
  }, [emblaApi]);

  const current = items[selectedIndex] ?? items[0];
  if (current === undefined) return null;

  return (
    <section
      aria-labelledby="home-featured-heading"
      className="relative isolate overflow-hidden bg-primary text-white"
    >
      {/* 지금 장의 사진을 크게 흐려 배경으로 깐다 — 사진이 없으면 흑연 판. */}
      {current.thumbnail !== null ? (
        <img
          key={current.id}
          src={current.thumbnail.url}
          alt=""
          className="absolute inset-0 -z-20 size-full scale-110 object-cover blur-2xl motion-safe:animate-fade-up"
        />
      ) : null}
      <div aria-hidden className="absolute inset-0 -z-10 bg-black/45" />

      <div className="grid min-h-[44rem] items-center gap-12 py-20 pl-5 sm:pl-8 lg:min-h-[52rem] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:py-28 xl:pl-14">
        <div className="pr-5 sm:pr-8 lg:pr-0">
          <h2 id="home-featured-heading" className="text-lg text-white/80 sm:text-2xl">
            SuperStructure 이달의 상품
          </h2>
          {/* key 로 장마다 새로 그려 문구가 떠오르며 바뀐다. */}
          <FeaturedCopy key={current.id} product={current} />
        </div>

        <div className="min-w-0">
          {/* biome-ignore lint/a11y/useSemanticElements: fieldset은 캐러셀 뷰포트에 맞지 않는다. */}
          <div
            ref={emblaRef}
            role="group"
            aria-roledescription="carousel"
            aria-label="이달의 상품 사진"
            className="overflow-hidden"
          >
            <ul className="-ml-6 flex touch-pan-y touch-pinch-zoom">
              {items.map((product, index) => (
                <li
                  key={product.id}
                  className="min-w-0 shrink-0 grow-0 basis-[82%] pl-6 sm:basis-[60%] lg:basis-[64%]"
                >
                  <FeaturedCard product={product} isActive={index === selectedIndex} />
                </li>
              ))}
            </ul>
          </div>

          {canSlide ? (
            <FeaturedControls
              selectedIndex={selectedIndex}
              count={items.length}
              onPrev={() => emblaApi?.scrollPrev()}
              onNext={() => emblaApi?.scrollNext()}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};
